import React, { useContext, useMemo, useCallback } from 'react';
import {
  PodSection,
  PodSpacer,
  ProductImage,
  ProductPod,
  ProductRating,
} from '@thd-olt-component-react/product-pod';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { Button } from '@one-thd/sui-atomic-components';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { dataModel } from './RetailMediaProductListingPod.dataModel';
import { useRetailMediaCarouselPodTelemetry, useProductPodRedirectUrl, useNodeRef } from '../../../hooks';
import { newRelicConstants } from '../../../constants';

const {
  string, bool, number, shape, arrayOf
} = proptypes;

const RetailMediaProductListingPod = (
  {
    product, storeId, position, noATCFulfillment
  }
) => {
  const experienceContext = useContext(ExperienceContext);
  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();

  // --
  const { redirectUrl } = useProductPodRedirectUrl({
    canonicalUrl: product?.identifiers?.canonicalUrl
  });

  // --
  const isMobile = useMemo(
    () => experienceContext.channel === 'mobile',
    [experienceContext.channel]
  );

  // -----------------
  // Telemetry
  // -----------------
  const {
    triggerClickOnShowNowButton,
    triggerClickOnImage,
    triggerClickOnHeader,
    triggerClickOnRating,
  } = useRetailMediaCarouselPodTelemetry({
    productPodNode,
    itemId: product?.itemId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    position,
    slotId: product?.info?.sponsoredMetadata?.slotId,
    podKpiName: newRelicConstants.PRODUCT_LISTING_AD,
  });

  // -----------------
  // Handlers
  // -----------------

  /**
   * Handler on: show-now-button 'click'
   */
  const handleClickOnShowNowButton = useCallback(
    (evt) => {
      // --
      evt.preventDefault();

      const action = newRelicConstants.CLICK_SRC_PRODUCT_SHOP_NOW_BUTTON;

      const productHeaderEventData = {
        podAction: action || 'product details',
        podAnchorSku: product.itemId,
        position: 0,
        parent: 'pla-on-pip',
      };

      window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', productHeaderEventData);

      triggerClickOnShowNowButton(); // new-relic

      window.location = redirectUrl; // redirect
    },
    [triggerClickOnShowNowButton, redirectUrl]
  );

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback(
    (event) => {
      event.preventDefault();
      // --
      triggerClickOnImage(); // new-relic
      window.location = redirectUrl; // redirect
    },
    [redirectUrl, triggerClickOnImage]
  );

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback(
    (event) => {
      event.preventDefault();
      // --
      triggerClickOnHeader(); // new-relic
      window.location = redirectUrl; // redirect
    },
    [redirectUrl, triggerClickOnHeader]
  );

  /**
   * Handler on: rating 'click'
   */
  const handleClickOnProductRating = useCallback((event) => {
    event.preventDefault();
    // --
    triggerClickOnRating(); // new-relic
    window.location = redirectUrl; // redirect
  }, [redirectUrl, triggerClickOnRating]);

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={product.itemId}
      data-testid="sponsored-product-listing-pod"
    >
      <ImpressionProvider
        data={{
          id: '',
          name: 'SponsoredProductListing',
          component: 'SponsoredProductListing',
          type: 'sponsoredproduct',
          category: ''
        }}
      >
        <ProductPod
          itemId={product.itemId}
          analyticsData={{ parent: 'pla-on-pip', position: 0 }}
          padding="sui-p-0"
          gap="sui-gap-0"
          render={(pod) => {
            return (
              <>
                <div className="sui-grid sui-grid-cols-12 sui-gap-6 sui-py-4 sui-px-5">
                  <div className="sui-col-span-6">
                    <ProductImage
                      itemId={pod.itemId}
                      showSecondaryImage={pod.showSecondaryImage}
                      onClick={handleClickOnProductImage}
                    />
                  </div>
                  <div className="sui-col-span-6">
                    <PodSection columnAlign alignTop noPadding>
                      <PodSpacer padding={['BOTTOM']}>
                        <ProductHeader
                          brand="inline"
                          brandFontSize="sm"
                          itemId={pod.itemId}
                          brandTitleMaxLine={1}
                          titleMaxLine={2}
                          disableShopThisCollection
                          onClick={handleClickOnProductHeader}
                        />
                      </PodSpacer>
                      <PodSpacer minHeight="26px">
                        <ProductRating
                          itemId={pod.itemId}
                          onlyReviewCount
                          onClick={handleClickOnProductRating}
                        />
                      </PodSpacer>
                      <PodSpacer minHeight="60px">
                        <Price
                          itemId={pod.itemId}
                          large={false}
                          storeId={storeId}
                          displayEachUom={false}
                          onSavingsCenterToggle={pod.onSavingsCenterToggle}
                          hideBadge
                          stackWasPrice
                        />
                      </PodSpacer>
                      {!isMobile && (
                        <PodSpacer padding={['TOP']}>
                          <Button variant="secondary" outline onClick={handleClickOnShowNowButton} fullWidth="true">
                            Shop Now
                          </Button>
                        </PodSpacer>
                      )}
                    </PodSection>
                  </div>
                </div>
              </>
            );
          }}
        />
      </ImpressionProvider>

    </div >
  );
};

RetailMediaProductListingPod.propTypes = {
  product: shape({
    itemId: string.isRequired,
    identifiers: shape({
      canonicalUrl: string,
    }),
    info: shape({
      sponsoredBeacon: shape({
        onClickBeacons: arrayOf(string),
        onViewBeacons: arrayOf(string),
      }),
      sponsoredMetadata: shape({
        trackSource: string,
        campaignId: string,
        placementId: string,
        slotId: string,
        sponsoredId: string,
      }),
    }),
  }).isRequired,
  position: number.isRequired,
  noATCFulfillment: bool,
  storeId: string.isRequired,
};
RetailMediaProductListingPod.defaultProps = {
  noATCFulfillment: null,
};

RetailMediaProductListingPod.dataModel = dataModel;

export { RetailMediaProductListingPod };
